"use client";

/**
 * Parcours personnalisé affiché sous les offres de /pricing.
 *
 * Montre les formations réellement enchaînées pour atteindre l'objectif de la
 * personne (calculées par lib/recommendations.js), plutôt qu'un argument
 * marketing. Deux rendus : une seule étape (n8n n'a aucun prérequis, et c'est la
 * cible de sept objectifs) donne une carte unique, deux ou plus donnent une
 * frise reliée.
 */

import { Fragment } from "react";
import Image from "next/image";
import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

function CourseCard({ course, isTarget }) {
    return (
        <div
            className={`flex items-center gap-3 rounded-xl border p-4 sm:w-52 ${
                isTarget
                    ? "border-primary/40 bg-primary/5 dark:border-primary/30 dark:bg-primary/10"
                    : "border-gray-200 bg-white dark:border-grayDark-800 dark:bg-grayDark-900"
            }`}
        >
            <Image
                src={course.image}
                alt=""
                width={40}
                height={40}
                className="w-10 h-10 shrink-0 object-contain"
            />
            <div className="min-w-0">
                <p className="mb-0 text-sm font-semibold text-gray-900 dark:text-white leading-snug">
                    {course.title}
                </p>
                <p className="mb-0 text-xs text-gray-500 dark:text-grayDark-400">
                    {course.duration}
                </p>
            </div>
        </div>
    );
}

export default function PricingPersonalPath({
    path,
    goalLabelKey,
    otherCoursesCount,
    onStart,
}) {
    const t = useTranslations();

    if (!path || path.length === 0) return null;

    const isSingle = path.length === 1;
    const goalLabel = t(goalLabelKey);

    return (
        <section className="px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20">
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="max-w-5xl mx-auto rounded-2xl border border-gray-200 dark:border-grayDark-800 bg-gray-50/70 dark:bg-grayDark-900/40 p-6 sm:p-8"
            >
                <div className="text-center mb-6">
                    <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                        {isSingle
                            ? t("pricing_path_single_title", {
                                  goal: goalLabel,
                              })
                            : t("pricing_path_title", { goal: goalLabel })}
                    </h2>
                    <p className="mb-0 text-sm sm:text-base text-gray-500 dark:text-grayDark-400">
                        {isSingle
                            ? t("pricing_path_single_subtitle")
                            : t("pricing_path_subtitle", {
                                  count: path.length,
                              })}
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-center gap-3">
                    {path.map((course, index) => (
                        <Fragment key={course.slug}>
                            {index > 0 && (
                                <SolarIcon
                                    name="alt-arrow-down-linear"
                                    className="w-4 h-4 text-gray-300 dark:text-grayDark-600 shrink-0 sm:hidden"
                                />
                            )}
                            {index > 0 && (
                                <SolarIcon
                                    name="alt-arrow-right-linear"
                                    className="w-4 h-4 text-gray-300 dark:text-grayDark-600 shrink-0 hidden sm:block"
                                />
                            )}
                            <CourseCard
                                course={course}
                                isTarget={index === path.length - 1}
                            />
                        </Fragment>
                    ))}
                </div>

                {otherCoursesCount > 0 && (
                    <p className="mt-6 mb-0 text-center text-sm text-gray-500 dark:text-grayDark-400">
                        {t("pricing_path_others", {
                            count: otherCoursesCount,
                        })}
                    </p>
                )}

                <div className="mt-6 flex justify-center">
                    <Button onClick={onStart}>{t("pricing_path_cta")}</Button>
                </div>
            </motion.div>
        </section>
    );
}
